import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Tooltip,
  message,
  Popconfirm,
  Drawer,
  Descriptions,
  Statistic,
  Row,
  Col,
  InputNumber,
  Collapse,
  Alert,
  Spin,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  SendOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import { adminApi } from '@/services/api';
import type {
  Webhook,
  WebhookEventType,
  WebhookStatus,
  WebhookLog,
  WebhookStats,
  CreateWebhookRequest,
} from '@/types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { AxiosError } from 'axios';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { Panel } = Collapse;

// Event type descriptions
const EVENT_DESCRIPTIONS: Record<string, string> = {
  'user.registered': 'Triggered when a new user registers',
  'user.login': 'Triggered when a user logs in',
  'user.logout': 'Triggered when a user logs out',
  'user.profile_updated': 'Triggered when user profile is updated',
  'user.account_linked': 'Triggered when user links an external account',
  'purchase.initiated': 'Triggered when a purchase is initiated',
  'purchase.completed': 'Triggered when a purchase is completed successfully',
  'purchase.failed': 'Triggered when a purchase fails',
  'purchase.refunded': 'Triggered when a purchase is refunded',
  'subscription.started': 'Triggered when a subscription starts',
  'subscription.renewed': 'Triggered when a subscription renews',
  'subscription.cancelled': 'Triggered when a subscription is cancelled',
  'subscription.expired': 'Triggered when a subscription expires',
  'match.started': 'Triggered when a match starts',
  'match.completed': 'Triggered when a match is completed',
  'match.abandoned': 'Triggered when a match is abandoned',
  'tournament.joined': 'Triggered when user joins a tournament',
  'tournament.completed': 'Triggered when user completes a tournament',
  'tournament.reward_claimed': 'Triggered when tournament reward is claimed',
  'progression.level_up': 'Triggered when user levels up',
  'progression.achievement_unlocked': 'Triggered when an achievement is unlocked',
  'progression.division_promoted': 'Triggered when user is promoted to a higher division',
  'progression.division_demoted': 'Triggered when user is demoted to a lower division',
  'economy.coins_earned': 'Triggered when user earns coins',
  'economy.coins_spent': 'Triggered when user spends coins',
  'economy.gems_earned': 'Triggered when user earns gems',
  'economy.gems_spent': 'Triggered when user spends gems',
  'card.acquired': 'Triggered when user acquires a new player card',
  'card.upgraded': 'Triggered when a player card is upgraded',
  'card.evolved': 'Triggered when a player card is evolved',
  'season_pass.purchased': 'Triggered when season pass is purchased',
  'season_pass.tier_unlocked': 'Triggered when a season pass tier is unlocked',
  'daily.login': 'Triggered on daily login',
  'daily.challenge_completed': 'Triggered when daily challenge is completed',
  'daily.reward_claimed': 'Triggered when daily reward is claimed',
};

// Group events by category
const EVENT_CATEGORIES: Record<string, WebhookEventType[]> = {
  'User Events': [
    'user.registered',
    'user.login',
    'user.logout',
    'user.profile_updated',
    'user.account_linked',
  ],
  'Purchase Events': [
    'purchase.initiated',
    'purchase.completed',
    'purchase.failed',
    'purchase.refunded',
  ],
  'Subscription Events': [
    'subscription.started',
    'subscription.renewed',
    'subscription.cancelled',
    'subscription.expired',
  ],
  'Match Events': ['match.started', 'match.completed', 'match.abandoned'],
  'Tournament Events': ['tournament.joined', 'tournament.completed', 'tournament.reward_claimed'],
  'Progression Events': [
    'progression.level_up',
    'progression.achievement_unlocked',
    'progression.division_promoted',
    'progression.division_demoted',
  ],
  'Economy Events': [
    'economy.coins_earned',
    'economy.coins_spent',
    'economy.gems_earned',
    'economy.gems_spent',
  ],
  'Card Events': ['card.acquired', 'card.upgraded', 'card.evolved'],
  'Season Pass Events': ['season_pass.purchased', 'season_pass.tier_unlocked'],
  'Daily Events': ['daily.login', 'daily.challenge_completed', 'daily.reward_claimed'],
};

export const WebhooksPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // Drawer states
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
  const [webhookStats, setWebhookStats] = useState<WebhookStats | null>(null);
  const [webhookLogs, setWebhookLogs] = useState<WebhookLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Test result modal
  const [testResultModalOpen, setTestResultModalOpen] = useState(false);
  const [testResult, setTestResult] = useState<WebhookLog | null>(null);
  const [testing, setTesting] = useState<string | null>(null);

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getWebhooks();
      const data = response.data;
      setWebhooks(Array.isArray(data) ? data : []);
    } catch (error) {
      message.error('Failed to load webhooks');
      setWebhooks([]); // Ensure empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingWebhook(null);
    form.resetFields();
    form.setFieldsValue({
      authType: 'none',
      apiKeyHeader: 'X-API-Key',
      signatureHeader: 'X-Webhook-Signature',
      timeoutMs: 30000,
      retryCount: 3,
      retryDelayMs: 1000,
      maxConsecutiveFailures: 10,
      events: [],
    });
    setIsModalOpen(true);
  };

  const handleEdit = (webhook: Webhook) => {
    setEditingWebhook(webhook);
    form.setFieldsValue({
      name: webhook.name,
      description: webhook.description,
      url: webhook.url,
      authType: webhook.authType,
      apiKey: webhook.apiKey,
      secretKey: webhook.secretKey,
      apiKeyHeader: webhook.apiKeyHeader,
      signatureHeader: webhook.signatureHeader,
      events: webhook.events,
      timeoutMs: webhook.timeoutMs,
      retryCount: webhook.retryCount,
      retryDelayMs: webhook.retryDelayMs,
      maxConsecutiveFailures: webhook.maxConsecutiveFailures,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (values: CreateWebhookRequest) => {
    setSubmitting(true);
    try {
      if (editingWebhook) {
        await adminApi.updateWebhook(editingWebhook.id, values);
        message.success('Webhook updated successfully');
      } else {
        await adminApi.createWebhook(values);
        message.success('Webhook created successfully');
      }
      setIsModalOpen(false);
      fetchWebhooks();
      fetchWebhooks();
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      message.error(err.response?.data?.message || 'Failed to save webhook');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await adminApi.deleteWebhook(id);
      message.success('Webhook deleted successfully');
      fetchWebhooks();
    } catch (error) {
      message.error('Failed to delete webhook');
    }
  };

  const handleToggleStatus = async (webhook: Webhook) => {
    try {
      if (webhook.status === 'active') {
        await adminApi.deactivateWebhook(webhook.id);
        message.success('Webhook deactivated');
      } else {
        await adminApi.activateWebhook(webhook.id);
        message.success('Webhook activated');
      }
      fetchWebhooks();
    } catch (error) {
      message.error('Failed to update webhook status');
    }
  };

  const handleTest = async (webhook: Webhook) => {
    setTesting(webhook.id);
    try {
      const response = await adminApi.testWebhook(webhook.id);
      setTestResult(response.data);
      setTestResultModalOpen(true);
      setTestResult(response.data);
      setTestResultModalOpen(true);
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      message.error(err.response?.data?.message || 'Failed to send test webhook');
    } finally {
      setTesting(null);
    }
  };

  const handleViewDetails = async (webhook: Webhook) => {
    setSelectedWebhook(webhook);
    setDetailsDrawerOpen(true);
    setLogsLoading(true);

    try {
      const [statsRes, logsRes] = await Promise.all([
        adminApi.getWebhookStats(webhook.id),
        adminApi.getWebhookLogs(webhook.id, { limit: 20 }),
      ]);
      setWebhookStats(statsRes.data);
      setWebhookLogs(logsRes.data.data || []);
    } catch (error) {
      message.error('Failed to load webhook details');
    } finally {
      setLogsLoading(false);
    }
  };

  const getStatusTag = (status: WebhookStatus) => {
    const config = {
      active: { color: 'green', icon: <CheckCircleOutlined /> },
      inactive: { color: 'default', icon: <PauseCircleOutlined /> },
      suspended: { color: 'red', icon: <ExclamationCircleOutlined /> },
    };
    return (
      <Tag color={config[status].color} icon={config[status].icon}>
        {status.toUpperCase()}
      </Tag>
    );
  };

  const getLogStatusTag = (status: string) => {
    const config: Record<string, { color: string }> = {
      success: { color: 'green' },
      failed: { color: 'red' },
      pending: { color: 'blue' },
      retrying: { color: 'orange' },
    };
    return <Tag color={config[status]?.color || 'default'}>{status.toUpperCase()}</Tag>;
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Webhook) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          {record.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.description}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: (url: string) => (
        <Tooltip title={url}>
          <Text code style={{ fontSize: 12 }}>
            {url.length > 50 ? `${url.substring(0, 50)}...` : url}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Events',
      dataIndex: 'events',
      key: 'events',
      width: 120,
      render: (events: WebhookEventType[]) => (
        <Tooltip title={events.join(', ')}>
          <Tag color="blue">{events.length} events</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: WebhookStatus) => getStatusTag(status),
    },
    {
      title: 'Stats',
      key: 'stats',
      width: 150,
      render: (_: unknown, record: Webhook) => (
        <Space size={4}>
          <Tooltip title="Successful deliveries">
            <Tag color="green">{record.totalSuccessCount}</Tag>
          </Tooltip>
          <Tooltip title="Failed deliveries">
            <Tag color="red">{record.totalFailureCount}</Tag>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Last Activity',
      key: 'lastActivity',
      width: 150,
      render: (_: unknown, record: Webhook) => {
        const lastActivity = record.lastSuccessAt || record.lastFailureAt;
        if (!lastActivity) return <Text type="secondary">Never</Text>;
        return (
          <Tooltip title={dayjs(lastActivity).format('YYYY-MM-DD HH:mm:ss')}>
            <Text>{dayjs(lastActivity).fromNow()}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_: unknown, record: Webhook) => (
        <Space>
          <Tooltip title="View Details">
            <Button type="text" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)} />
          </Tooltip>
          <Tooltip title={record.status === 'active' ? 'Deactivate' : 'Activate'}>
            <Button
              type="text"
              icon={record.status === 'active' ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={() => handleToggleStatus(record)}
            />
          </Tooltip>
          <Tooltip title="Send Test">
            <Button
              type="text"
              icon={<SendOutlined />}
              loading={testing === record.id}
              onClick={() => handleTest(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Popconfirm
            title="Delete this webhook?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const logColumns = [
    {
      title: 'Event',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (event: string) => <Tag>{event}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getLogStatusTag(status),
    },
    {
      title: 'Response',
      dataIndex: 'responseStatusCode',
      key: 'responseStatusCode',
      render: (code: number) =>
        code ? <Tag color={code >= 200 && code < 300 ? 'green' : 'red'}>{code}</Tag> : '-',
    },
    {
      title: 'Duration',
      dataIndex: 'durationMs',
      key: 'durationMs',
      render: (ms: number) => (ms ? `${ms}ms` : '-'),
    },
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => (
        <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm:ss')}>{dayjs(date).fromNow()}</Tooltip>
      ),
    },
  ];

  const authType = Form.useWatch('authType', form);

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <ApiOutlined style={{ marginRight: 8 }} />
              Webhooks
            </Title>
            <Text type="secondary">
              Configure webhooks to send real-time events to third-party services
            </Text>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchWebhooks}>
                Refresh
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                Create Webhook
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <Card>
        <Table
          dataSource={webhooks}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{
            emptyText: (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No webhooks configured">
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                  Create Your First Webhook
                </Button>
              </Empty>
            ),
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingWebhook ? 'Edit Webhook' : 'Create Webhook'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            authType: 'none',
            apiKeyHeader: 'X-API-Key',
            signatureHeader: 'X-Webhook-Signature',
            timeoutMs: 30000,
            retryCount: 3,
            retryDelayMs: 1000,
            maxConsecutiveFailures: 10,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Please enter a name' }]}
              >
                <Input placeholder="e.g., Analytics Platform" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="description" label="Description">
                <Input placeholder="e.g., Sends events to our analytics service" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="url"
            label="Webhook URL"
            rules={[
              { required: true, message: 'Please enter a URL' },
              { type: 'url', message: 'Please enter a valid URL' },
            ]}
          >
            <Input placeholder="https://api.example.com/webhooks/wcc4" />
          </Form.Item>

          <Form.Item
            name="events"
            label="Events"
            rules={[{ required: true, message: 'Please select at least one event' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select events to subscribe to"
              style={{ width: '100%' }}
              maxTagCount={5}
              optionFilterProp="label"
            >
              {Object.entries(EVENT_CATEGORIES).map(([category, events]) => (
                <Select.OptGroup key={category} label={category}>
                  {events.map((event) => (
                    <Select.Option key={event} value={event} label={event}>
                      <Space>
                        <Text>{event}</Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          - {EVENT_DESCRIPTIONS[event]}
                        </Text>
                      </Space>
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              ))}
            </Select>
          </Form.Item>

          <Collapse ghost defaultActiveKey={['auth']}>
            <Panel header="Authentication" key="auth">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="authType" label="Auth Type">
                    <Select>
                      <Select.Option value="none">None</Select.Option>
                      <Select.Option value="api_key">API Key</Select.Option>
                      <Select.Option value="secret_key">Secret Key (HMAC)</Select.Option>
                      <Select.Option value="both">Both</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>

                {(authType === 'api_key' || authType === 'both') && (
                  <>
                    <Col span={8}>
                      <Form.Item name="apiKey" label="API Key">
                        <Input.Password placeholder="sk_live_..." />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="apiKeyHeader" label="API Key Header">
                        <Input placeholder="X-API-Key" />
                      </Form.Item>
                    </Col>
                  </>
                )}

                {(authType === 'secret_key' || authType === 'both') && (
                  <>
                    <Col span={8}>
                      <Form.Item name="secretKey" label="Secret Key">
                        <Input.Password placeholder="whsec_..." />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="signatureHeader" label="Signature Header">
                        <Input placeholder="X-Webhook-Signature" />
                      </Form.Item>
                    </Col>
                  </>
                )}
              </Row>
            </Panel>

            <Panel header="Advanced Settings" key="advanced">
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item name="timeoutMs" label="Timeout (ms)">
                    <InputNumber min={1000} max={60000} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="retryCount" label="Retry Count">
                    <InputNumber min={0} max={10} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="retryDelayMs" label="Retry Delay (ms)">
                    <InputNumber min={100} max={60000} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="maxConsecutiveFailures" label="Max Failures">
                    <InputNumber min={1} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          </Collapse>

          <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {editingWebhook ? 'Update' : 'Create'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Details Drawer */}
      <Drawer
        title={
          <Space>
            <ApiOutlined />
            {selectedWebhook?.name}
          </Space>
        }
        placement="right"
        width={600}
        onClose={() => setDetailsDrawerOpen(false)}
        open={detailsDrawerOpen}
      >
        {selectedWebhook && (
          <div>
            <Descriptions column={1} bordered size="small" style={{ marginBottom: 24 }}>
              <Descriptions.Item label="URL">
                <Text code copyable>
                  {selectedWebhook.url}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {getStatusTag(selectedWebhook.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Auth Type">
                <Tag>{selectedWebhook.authType.toUpperCase()}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Events">
                <div style={{ maxHeight: 100, overflow: 'auto' }}>
                  <Space wrap size={4}>
                    {selectedWebhook.events.map((event) => (
                      <Tag key={event} style={{ fontSize: 11 }}>
                        {event}
                      </Tag>
                    ))}
                  </Space>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {dayjs(selectedWebhook.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            </Descriptions>

            {webhookStats && (
              <Card title="Statistics" size="small" style={{ marginBottom: 24 }}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic title="Total Deliveries" value={webhookStats.totalDeliveries} />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Success Rate"
                      value={webhookStats.successRate}
                      suffix="%"
                      precision={1}
                      valueStyle={{
                        color: webhookStats.successRate >= 90 ? '#3f8600' : '#cf1322',
                      }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Avg Duration"
                      value={webhookStats.averageDurationMs}
                      suffix="ms"
                      precision={0}
                    />
                  </Col>
                </Row>
              </Card>
            )}

            <Card
              title="Recent Logs"
              size="small"
              extra={
                <Button
                  type="link"
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => handleViewDetails(selectedWebhook)}
                  loading={logsLoading}
                >
                  Refresh
                </Button>
              }
            >
              {logsLoading ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <Spin />
                </div>
              ) : webhookLogs.length > 0 ? (
                <Table
                  dataSource={webhookLogs}
                  columns={logColumns}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  scroll={{ y: 300 }}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No delivery logs yet" />
              )}
            </Card>
          </div>
        )}
      </Drawer>

      {/* Test Result Modal */}
      <Modal
        title={
          <Space>
            <SendOutlined />
            Test Webhook Result
          </Space>
        }
        open={testResultModalOpen}
        onCancel={() => setTestResultModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setTestResultModalOpen(false)}>
            Close
          </Button>,
        ]}
        width={600}
      >
        {testResult && (
          <div>
            <Alert
              message={testResult.status === 'success' ? 'Webhook Delivered' : 'Delivery Failed'}
              description={
                testResult.status === 'success'
                  ? 'The test webhook was delivered successfully.'
                  : testResult.errorMessage || 'The webhook delivery failed.'
              }
              type={testResult.status === 'success' ? 'success' : 'error'}
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Status">
                {getLogStatusTag(testResult.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Response Code">
                {testResult.responseStatusCode ? (
                  <Tag
                    color={
                      testResult.responseStatusCode >= 200 && testResult.responseStatusCode < 300
                        ? 'green'
                        : 'red'
                    }
                  >
                    {testResult.responseStatusCode}
                  </Tag>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Duration">
                {testResult.durationMs ? `${testResult.durationMs}ms` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Attempt">
                {testResult.attemptNumber} / {testResult.maxAttempts}
              </Descriptions.Item>
              {testResult.errorMessage && (
                <Descriptions.Item label="Error">
                  <Text type="danger">{testResult.errorMessage}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            {testResult.responseBody && (
              <div style={{ marginTop: 16 }}>
                <Text strong>Response Body:</Text>
                <pre
                  style={{
                    background: '#f5f5f5',
                    padding: 12,
                    borderRadius: 4,
                    maxHeight: 200,
                    overflow: 'auto',
                    fontSize: 12,
                  }}
                >
                  {testResult.responseBody}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WebhooksPage;
